import type { Lead } from './LeadCard';

export type LeadStatus = 'new' | 'contacted' | 'demo_scheduled' | 'demo_done' | 'converted' | 'lost';

export interface StageRequirement {
  field: string;
  label: string;
  type: 'field' | 'activity' | 'checkbox';
  activityType?: 'call' | 'email' | 'meeting' | 'note';
}

export interface ValidationResult {
  canMove: boolean;
  missingRequirements: StageRequirement[];
  targetStatus: LeadStatus;
}

// Define what's required for each stage transition
const stageRequirements: Record<LeadStatus, StageRequirement[]> = {
  new: [], // No requirements to be in "new"
  contacted: [
    { field: 'activity_count', label: 'Log at least one activity (call, email, or meeting)', type: 'activity' },
  ],
  demo_scheduled: [
    { field: 'pain_point', label: 'Document the pain point', type: 'field' },
    { field: 'demo_date', label: 'Set demo date', type: 'field' },
    { field: 'demo_time', label: 'Set demo time', type: 'field' },
  ],
  demo_done: [
    { field: 'meeting_activity', label: 'Log a meeting activity for the demo', type: 'activity', activityType: 'meeting' },
  ],
  converted: [
    { field: 'is_decision_maker', label: 'Confirm this is the decision maker', type: 'checkbox' },
    { field: 'is_qualified', label: 'Mark lead as qualified', type: 'checkbox' },
  ],
  lost: [], // No requirements to mark as lost
};

// Status order for determining if moving forward or backward
const statusOrder: LeadStatus[] = ['new', 'contacted', 'demo_scheduled', 'demo_done', 'converted'];

export function getStatusIndex(status: LeadStatus): number {
  const index = statusOrder.indexOf(status);
  return index === -1 ? -1 : index; // lost is special, returns -1
}

export function isMovingForward(fromStatus: LeadStatus, toStatus: LeadStatus): boolean {
  if (toStatus === 'lost') return false; // Moving to lost is never "forward"
  const fromIndex = getStatusIndex(fromStatus);
  const toIndex = getStatusIndex(toStatus);
  return toIndex > fromIndex;
}

export function validateStageTransition(
  lead: Lead,
  targetStatus: LeadStatus,
  activityCount: number = 0,
  hasMeetingActivity: boolean = false
): ValidationResult {
  // Moving to lost is always allowed
  if (targetStatus === 'lost') {
    return { canMove: true, missingRequirements: [], targetStatus };
  }

  // Moving backward is always allowed (e.g., demo_scheduled back to contacted)
  if (!isMovingForward(lead.status, targetStatus)) {
    return { canMove: true, missingRequirements: [], targetStatus };
  }

  // Check requirements for the target status
  const requirements = stageRequirements[targetStatus];
  const missing: StageRequirement[] = [];

  for (const req of requirements) {
    let isMet = false;

    switch (req.type) {
      case 'field':
        // Check if the field has a value
        const value = lead[req.field as keyof Lead];
        isMet = value !== null && value !== undefined && value !== '';
        break;

      case 'checkbox':
        // Check if the checkbox is checked (value is 1)
        const checkboxValue = lead[req.field as keyof Lead];
        isMet = checkboxValue === 1;
        break;

      case 'activity':
        if (req.activityType === 'meeting') {
          isMet = hasMeetingActivity;
        } else {
          isMet = activityCount > 0;
        }
        break;
    }

    if (!isMet) {
      missing.push(req);
    }
  }

  return {
    canMove: missing.length === 0,
    missingRequirements: missing,
    targetStatus,
  };
}

// Get what's needed for the NEXT stage (for warning indicators)
export function getNextStageRequirements(lead: Lead): StageRequirement[] {
  const currentIndex = getStatusIndex(lead.status);
  if (currentIndex === -1 || currentIndex >= statusOrder.length - 1) {
    return []; // Already at converted or lost
  }

  const nextStatus = statusOrder[currentIndex + 1];
  return stageRequirements[nextStatus];
}

// Check what's missing for the next stage
export function getMissingForNextStage(
  lead: Lead,
  activityCount: number = 0,
  hasMeetingActivity: boolean = false
): StageRequirement[] {
  const currentIndex = getStatusIndex(lead.status);
  if (currentIndex === -1 || currentIndex >= statusOrder.length - 1) {
    return [];
  }

  const nextStatus = statusOrder[currentIndex + 1];
  const result = validateStageTransition(lead, nextStatus, activityCount, hasMeetingActivity);
  return result.missingRequirements;
}

// Human-readable status labels
export const statusLabels: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  demo_scheduled: 'Demo Scheduled',
  demo_done: 'Demo Done',
  converted: 'Converted',
  lost: 'Lost',
};
